import { useEffect, useRef, useState } from 'react'
import {
  flightsAPI,
  ScheduledFlight,
} from 'api/flightsAPI'

interface UseFlightLookupOptions {
  flightNumber: string | undefined
  date: string | undefined // ISO date string or Date-parseable string
  isArrival: boolean
  enabled?: boolean
}

export default function useFlightLookup({
  flightNumber,
  date,
  isArrival,
  enabled = true,
}: UseFlightLookupOptions) {
  const [flight, setFlight] = useState<ScheduledFlight | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const lastRequestRef = useRef('')

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    const trimmed = flightNumber?.trim() || ''

    // Need at least 3 chars (e.g. "BA1") and a date
    if (!enabled || trimmed.length < 3 || !date) {
      setFlight(null)
      setError(false)
      return
    }

    const dateStr = date.slice(0, 10) // YYYY-MM-DD
    const requestKey = `${trimmed}-${dateStr}-${isArrival}`

    // Don't re-fetch the same request
    if (requestKey === lastRequestRef.current) return

    debounceRef.current = setTimeout(async () => {
      lastRequestRef.current = requestKey
      setIsLoading(true)
      setError(false)

      try {
        const result = await flightsAPI.getScheduledFlight({
          flightNumber: trimmed,
          date: dateStr,
          isArrival,
        })
        setFlight(result)
      } catch {
        setError(true)
        setFlight(null)
      } finally {
        setIsLoading(false)
      }
    }, 800)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [flightNumber, date, isArrival, enabled])

  return { flight, isLoading, error }
}
